import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarIcon, Check, ChevronDown, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { ITEM_LABEL, calendarAccents, lockedUntilCopy } from "./itemTypes";
import { MyEventRow, eventsByDay, useMyEvents } from "@/components/events/MyEvents";

type CalItem = {
  activityId: string;
  courseId: string;
  courseTitle: string;
  type: string;
  title: string | null;
  dueAt: string | null;
  pointsPossible: number | null;
  status: "not_started" | "in_progress" | "completed";
  locked?: boolean;
  startWith?: { activityId: string; title: string | null } | null;
};

type SchoolEvent = {
  id: string;
  date: string;
  kind: string;
  title: string;
  note: string | null;
};

type CalData = {
  items: CalItem[];
  undated: CalItem[];
  courses: { id: string; title: string }[];
  schoolEvents?: SchoolEvent[];
};

const SCHOOL_KIND: Record<string, string> = {
  no_school: "No school",
  holiday: "Holiday",
  early_release: "Early release",
  testing: "Testing",
  other: "School",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_IN_CELL = 3;

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function dayKey(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromIsoLocal(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function monthGrid(month: Date) {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  start.setHours(0, 0, 0, 0);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const end = new Date(last);
  end.setDate(last.getDate() + (6 - last.getDay()));
  end.setHours(23, 59, 59, 999);
  const days: Date[] = [];
  for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    days.push(new Date(cursor));
  }
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return { start, end: new Date(end.getTime() + 1), weeks };
}

function openItem(navigate: ReturnType<typeof useNavigate>, item: CalItem) {
  if (!item.locked) return navigate(`/student/activity/${item.activityId}`);
  if (item.startWith) return navigate(`/student/activity/${item.startWith.activityId}`);
  return navigate(`/student/course/${item.courseId}`);
}

/**
 * Month view of dated published work, plus unfinished items with no due date.
 * Same lock rule as Today: a locked due still shows, and the click goes to
 * the step that unlocks it.
 */
export default function StudentCalendar() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<string | null>(null);
  const [data, setData] = useState<CalData | null>(null);
  const [error, setError] = useState("");
  const [hiddenCourses, setHiddenCourses] = useState<Record<string, boolean>>({});
  const [undatedOpen, setUndatedOpen] = useState(true);

  const grid = useMemo(() => monthGrid(month), [month]);
  const todayKey = dayKey(new Date());
  const lastDay = useMemo(() => new Date(grid.end.getTime() - 1), [grid.end]);
  const mine = useMyEvents(dayKey(grid.start), dayKey(lastDay));
  const mineByDay = useMemo(() => eventsByDay(mine.events), [mine.events]);

  useEffect(() => {
    let cancelled = false;
    const from = grid.start.toISOString();
    const to = grid.end.toISOString();
    setError("");
    setData(null);
    api<{ success: boolean; data: CalData }>(
      `/api/student/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    )
      .then((env) => {
        if (!cancelled) setData(env.data);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [grid.start, grid.end]);

  const visible = (item: CalItem) => !hiddenCourses[item.courseId];
  const byDay = useMemo(() => {
    const map = new Map<string, CalItem[]>();
    for (const item of data?.items || []) {
      if (hiddenCourses[item.courseId] || !item.dueAt) continue;
      const when = fromIsoLocal(item.dueAt);
      if (!when) continue;
      const key = dayKey(when);
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    }
    return map;
  }, [data, hiddenCourses]);

  const schoolByDay = useMemo(() => {
    const map = new Map<string, SchoolEvent[]>();
    for (const event of data?.schoolEvents || []) {
      if (!event.date) continue;
      const list = map.get(event.date) || [];
      list.push(event);
      map.set(event.date, list);
    }
    return map;
  }, [data]);

  useEffect(() => {
    if (!data || selected) return;
    const now = new Date();
    if (month.getMonth() === now.getMonth() && month.getFullYear() === now.getFullYear()) {
      setSelected(todayKey);
    }
  }, [data, month, selected, todayKey]);

  const selectedItems = selected ? byDay.get(selected) || [] : [];
  const selectedSchool = selected ? schoolByDay.get(selected) || [] : [];
  const selectedMine = selected ? mineByDay.get(selected) || [] : [];
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const accents = useMemo(() => calendarAccents(data?.courses || []), [data]);
  const colorOf = (courseId: string) => accents[courseId] || "#2563EB";
  const legend = (data?.courses || []).filter(
    (course) =>
      (data?.items || []).some((item) => item.courseId === course.id) ||
      (data?.undated || []).some((item) => item.courseId === course.id),
  );
  const undated = (data?.undated || []).filter(visible);

  if (error) {
    return (
      <div className="student-page">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="student-page student-page--wide student-page--cal">
      <div className="student-page-head">
        <span className="student-eyebrow">
          <CalendarIcon size={12} aria-hidden /> Calendar
        </span>
        <div className="student-cal-head">
          <h1>{monthLabel}</h1>
          <div className="student-cal-toolbar">
            <button
              type="button"
              className="student-text-link"
              onClick={() => {
                const now = startOfMonth(new Date());
                setMonth(now);
                setSelected(todayKey);
              }}
            >
              Today
            </button>
            <button
              type="button"
              className="student-cal-nav"
              aria-label="Previous month"
              onClick={() => {
                setSelected(null);
                setMonth((m) => addMonths(m, -1));
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="student-cal-nav"
              aria-label="Next month"
              onClick={() => {
                setSelected(null);
                setMonth((m) => addMonths(m, 1));
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {!data ? (
        <CalendarOpeningSkeleton month={month} weeks={grid.weeks} />
      ) : (
        <div className="student-cal">
          <div className="student-cal__grid-wrap">
            <div className="student-cal-grid" role="grid" aria-label={monthLabel}>
              {WEEKDAYS.map((d) => (
                <div key={d} className="student-cal-dow" role="columnheader">
                  {d}
                </div>
              ))}
              {grid.weeks.flat().map((day) => {
                const key = dayKey(day);
                const inMonth = day.getMonth() === month.getMonth();
                const items = byDay.get(key) || [];
                const school = schoolByDay.get(key) || [];
                const own = mineByDay.get(key) || [];
                const count = items.length + school.length + own.length;
                const extra = Math.max(0, count - MAX_IN_CELL);
                const schoolShown = school.slice(0, MAX_IN_CELL);
                const ownShown = own.slice(0, Math.max(0, MAX_IN_CELL - schoolShown.length));
                const shown = items.slice(0, Math.max(0, MAX_IN_CELL - schoolShown.length - ownShown.length));
                const isToday = key === todayKey;
                const isSelected = key === selected;
                return (
                  <button
                    key={key}
                    type="button"
                    role="gridcell"
                    className={`student-cal-cell${inMonth ? "" : " is-outside"}${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`}
                    onClick={() => setSelected(key)}
                    aria-label={`${day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${count ? `, ${count} on this day` : ""}`}
                  >
                    <span className="student-cal-cell__n">{day.getDate()}</span>
                    <span className="student-cal-cell__items">
                      {schoolShown.map((event) => (
                        <span
                          key={event.id}
                          className={`student-cal-pill student-cal-pill--school${event.kind === "no_school" || event.kind === "holiday" ? " is-off" : ""}`}
                        >
                          <span className="student-cal-pill__t">{event.title}</span>
                        </span>
                      ))}
                      {ownShown.map((event) => (
                        <span key={event.id} className="student-cal-pill student-cal-pill--event">
                          <span className="student-cal-pill__t">{event.title}</span>
                        </span>
                      ))}
                      {shown.map((item) => (
                        <span
                          key={item.activityId}
                          className={`student-cal-pill${item.status === "completed" ? " is-done" : ""}${item.locked ? " is-locked" : ""}`}
                          style={{ background: colorOf(item.courseId) }}
                        >
                          {item.status === "completed" ? (
                            <Check size={10} aria-hidden />
                          ) : item.locked ? (
                            <Lock size={10} aria-hidden />
                          ) : null}
                          <span className="student-cal-pill__t">{item.title || "Untitled"}</span>
                        </span>
                      ))}
                      {extra > 0 ? <span className="student-cal-more">+{extra} more</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>

            {selected ? (
              <div className="student-card student-cal-day" style={{ marginTop: 14 }}>
                <span className="student-eyebrow">
                  {fromIsoLocal(`${selected}T12:00:00`)?.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                {selectedSchool.length === 0 && selectedItems.length === 0 && selectedMine.length === 0 ? (
                  <p className="student-row__sub" style={{ marginTop: 8 }}>
                    Nothing on this day.
                  </p>
                ) : (
                  <>
                    {selectedSchool.map((event) => (
                      <div key={event.id} className="student-row">
                        <span>
                          <strong>{event.title}</strong>
                          <span className="student-row__sub">
                            {SCHOOL_KIND[event.kind] || "School"}
                            {event.note ? ` · ${event.note}` : ""}
                          </span>
                        </span>
                      </div>
                    ))}
                    {selectedMine.length ? (
                      <div className="student-cal-events">
                        {selectedMine.map((event) => (
                          <MyEventRow
                            key={event.id}
                            event={event}
                            canRespond={mine.canRespond}
                            onChange={mine.replace}
                          />
                        ))}
                      </div>
                    ) : null}
                    {selectedItems.map((item) => (
                    <CalRow
                      key={item.activityId}
                      item={item}
                      accent={colorOf(item.courseId)}
                      onOpen={() => openItem(navigate, item)}
                    />
                    ))}
                  </>
                )}
              </div>
            ) : null}
          </div>

          <aside className="student-cal-side">
            {legend.length ? (
              <div className="student-card">
                <span className="student-eyebrow">Classes</span>
                <ul className="student-cal-legend">
                  {legend.map((course) => (
                    <li key={course.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={!hiddenCourses[course.id]}
                          onChange={() =>
                            setHiddenCourses((prev) => ({ ...prev, [course.id]: !prev[course.id] }))
                          }
                        />
                        <span
                          className="student-cal-dot"
                          style={{ background: colorOf(course.id) }}
                          aria-hidden
                        />
                        {course.title}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="student-card student-cal-undated" style={{ marginTop: legend.length ? 12 : 0 }}>
              <button
                type="button"
                className="student-cal-fold"
                aria-expanded={undatedOpen}
                onClick={() => setUndatedOpen((open) => !open)}
              >
                <ChevronDown
                  size={14}
                  aria-hidden
                  className={`student-cal-fold__chev${undatedOpen ? "" : " is-closed"}`}
                />
                <span className="student-eyebrow">No due date</span>
                {undated.length ? (
                  <span className="student-cal-fold__n">{undated.length}</span>
                ) : null}
              </button>
              {undatedOpen ? (
                undated.length === 0 ? (
                  <p className="student-row__sub" style={{ marginTop: 8 }}>
                    Nothing waiting without a due date.
                  </p>
                ) : (
                  <div className="student-cal-undated__list">
                    {undated.map((item) => (
                      <UndatedRow
                        key={item.activityId}
                        item={item}
                        accent={colorOf(item.courseId)}
                        onOpen={() => openItem(navigate, item)}
                      />
                    ))}
                  </div>
                )
              ) : null}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function CalendarOpeningSkeleton({ month, weeks }: { month: Date; weeks: Date[][] }) {
  const days = weeks.flat();
  return (
    <div className="student-cal" aria-busy="true" aria-live="polite">
      <span className="student-vh">Loading your calendar</span>
      <div className="student-cal__grid-wrap">
        <div className="student-cal-grid student-cal-grid--ghost" role="presentation">
          {WEEKDAYS.map((d) => (
            <div key={d} className="student-cal-dow">
              {d}
            </div>
          ))}
          {days.map((day, i) => {
            const inMonth = day.getMonth() === month.getMonth();
            return (
              <div
                key={dayKey(day)}
                className={`student-cal-cell${inMonth ? "" : " is-outside"}`}
              >
                <span className="student-cal-cell__n">{day.getDate()}</span>
                <span className="student-cal-cell__items">
                  {inMonth && i % 4 === 1 ? <span className="student-ghost student-ghost--cal-pill" /> : null}
                  {inMonth && i % 6 === 2 ? (
                    <span className="student-ghost student-ghost--cal-pill student-ghost--w70" />
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <aside className="student-cal-side">
        <div className="student-ghost-card" style={{ marginTop: 0 }}>
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--line student-ghost--w70" />
          <div className="student-ghost student-ghost--line student-ghost--w90" />
        </div>
        <div className="student-ghost-card">
          <div className="student-ghost student-ghost--chip" />
          <div className="student-ghost student-ghost--line student-ghost--w70" />
          <div className="student-ghost student-ghost--line student-ghost--w80" />
          <div className="student-ghost student-ghost--meta" />
        </div>
      </aside>
    </div>
  );
}

function CalRow({
  item,
  accent,
  onOpen,
}: {
  item: CalItem;
  accent: string;
  onOpen: () => void;
}) {
  return (
    <button type="button" className="student-row student-cal-dayrow" onClick={onOpen}>
      <span className="student-cal-dayrow__swatch" style={{ background: accent }} aria-hidden />
      <span style={{ textAlign: "left", minWidth: 0 }}>
        <strong style={{ color: accent }}>{item.title || "Untitled activity"}</strong>
        <span className="student-row__sub">
          {item.courseTitle} · {ITEM_LABEL[item.type] || item.type}
          {item.locked ? ` · ${lockedUntilCopy(item.startWith?.title)}` : ""}
          {item.status === "completed" ? " · Complete" : ""}
        </span>
      </span>
      {item.status === "completed" ? (
        <Check size={14} aria-hidden />
      ) : item.locked ? (
        <Lock size={14} aria-hidden />
      ) : null}
    </button>
  );
}

function UndatedRow({
  item,
  accent,
  onOpen,
}: {
  item: CalItem;
  accent: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="student-cal-event"
      style={{ color: accent }}
      onClick={onOpen}
    >
      <span className="student-cal-event__title">
        {item.locked ? <Lock size={11} aria-hidden /> : null}
        <span className="student-cal-event__name">{item.title || "Untitled activity"}</span>
      </span>
      <span className="student-cal-event__meta">{item.courseTitle}</span>
    </button>
  );
}
